import * as svgIcons from "../icons/svg";
import { useMemo } from "react";
import { SvgIconProps } from "../Icon.types";
import { getClassNames } from "../../../helpers/classHelpers";
import styles from "../Icon.module.scss";
import { guid } from '../../../helpers/randomHelpers';

export default function Svg(props: SvgIconProps) {
    const { testId, isDisabled, isClickable, className, iconName, title, size = 24, borderColor = 'blue', borderSize = 0, theme, fill = 'black', style, onClick } = props;
    const key = useMemo(guid, []);

    const icon = useMemo(() => {
        const Node = svgIcons[iconName];

        const clickable = ((onClick || isClickable));

        setTimeout(() => {
            namespaceSvgDefs(key);
        }, 200);
        return <span
            key={key}
            id={key}
            className={styles.icon}
            style={{ color: "red" }}>
            <Node
                id={testId}
                title={title}
                fill="currentColor"
                style={style}
                tabIndex={(onClick || isClickable) ? 0 : undefined}
                onClick={onClick}
                className={
                    getClassNames([
                        className,
                        styles.icon,
                        fill && styles[fill],
                        clickable && styles.clickable,
                        size && styles[`size-${size}`],
                        fill && styles[`fill-${fill}`],
                        theme && styles[`theme-${theme}`],
                        !!borderSize && styles[`border-size-${borderSize}`],
                        borderColor && styles[`border-color-${borderColor}`]
                    ])
                }
                aria-disabled={isDisabled}
            />
        </span>;
    }, [key, borderColor, borderSize, className, fill, iconName, isClickable, isDisabled, onClick, size, style, testId, theme, title]);

    return icon;
}


export function namespaceSvgDefs(svgId: string): void {
    const svg = document.getElementById(svgId) as SVGSVGElement | null;
    if (!svg) {
        return;
    }

    const defs = svg.querySelector('defs');
    if (!defs) return;

    const idMap = new Map<string, string>();

    // 1. Collect and rename all IDs inside <defs>
    defs.querySelectorAll<SVGElement>('[id]').forEach(el => {
        const oldId = el.id;
        const newId = `${svgId}__${oldId}`;

        el.id = newId;
        idMap.set(oldId, newId);
    });

    if (idMap.size === 0) return;

    // Attributes that may reference IDs
    const refAttributes = [
        'fill',
        'stroke',
        'filter',
        'clip-path',
        'mask',
        'marker-start',
        'marker-mid',
        'marker-end',
        'href',
        'xlink:href'
    ];

    // Regex for url(#id)
    const urlRefRegex = /url\(#([^)]+)\)/g;

    // 2. Update references across the entire SVG
    svg.querySelectorAll<SVGElement>('*').forEach(el => {
        // Update attributes like fill="url(#foo)"
        refAttributes.forEach(attr => {
            const value = el.getAttribute(attr);
            if (!value) return;

            const updated = value.replace(urlRefRegex, (_, id) => {
                const mapped = idMap.get(id);
                return mapped ? `url(#${mapped})` : _;
            });

            if (updated !== value) {
                el.setAttribute(attr, updated);
            }
        });

        // Update inline style attribute
        const style = el.getAttribute('style');
        if (style) {
            const updatedStyle = style.replace(urlRefRegex, (_, id) => {
                const mapped = idMap.get(id);
                return mapped ? `url(#${mapped})` : _;
            });

            if (updatedStyle !== style) {
                el.setAttribute('style', updatedStyle);
            }
        }
    });
}
